/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'

import React from 'react'
import { View, Icon, Pressable } from '@components/atoms'
import { DndTree } from '@components/systems'
import { mono } from '@components/systems/backoffice/backoffice.tokens'

import {
	useAppSelector,
	useAppDispatch,
	accessControlActions,
	MenuTreeNodeType,
	CrudType,
	CrudStrType,
} from '@redux/index'
import { AccessControlType } from '@schemas'

interface PropsType {
	menuTreeList: MenuTreeNodeType[]
	accessControlList: AccessControlType[]
	isDisabled?: boolean
	isReadOnly?: boolean
}

/** 메뉴별 CRUD 체크 — 행마다 우측에 생성·읽기·수정·삭제 네 칸. 체크 = ink, 빈 칸 = lineInput */
export const AccessControlMenuList = (props: PropsType) => {
	const { menuTreeList, accessControlList } = props

	// redux dispatch
	const reduxDispatch = useAppDispatch()

	// redux state
	const crud: CrudType = useAppSelector(state => state.auth.currentAccess)

	// variables
	const isDisabled: boolean = props.isDisabled ?? false
	const isReadOnly = !crud.includes('U')

	const onAuthSelectorClick = ({
		accessControlId,
		menuId,
		prevCrud,
		crudType,
	}: {
		accessControlId: string
		menuId: string
		prevCrud: CrudStrType
		crudType: 'C' | 'R' | 'U' | 'D'
	}) => {
		const prevCrudArr: CrudType = prevCrud.split('') as CrudType
		const newCrudArr: CrudType = [...prevCrudArr]
		const crudIndex = newCrudArr.indexOf(crudType)

		if (crudIndex > -1) {
			newCrudArr.splice(crudIndex, 1)
		} else {
			newCrudArr.push(crudType)
		}

		if (newCrudArr.length > 0) {
			newCrudArr.sort((a: 'C' | 'R' | 'U' | 'D', b: 'C' | 'R' | 'U' | 'D') => {
				const defaultCrudArr: CrudType = ['C', 'R', 'U', 'D']
				const aIndex: number = defaultCrudArr.indexOf(a)
				const bIndex: number = defaultCrudArr.indexOf(b)

				const order = aIndex > bIndex ? 1 : aIndex < bIndex ? -1 : 0

				return order
			})
		}

		const crudStr: CrudStrType = newCrudArr.join('') as CrudStrType

		if (accessControlId === 'new') {
			// insert
			reduxDispatch(
				accessControlActions.ADD_ACCESS_CONTROL({
					menuId: menuId,
					crud: crudStr,
				}),
			)
		} else if (crudStr === '') {
			// delete
			reduxDispatch(accessControlActions.DELETE_ACCESS_CONTROL(accessControlId))
		} else {
			// update
			reduxDispatch(
				accessControlActions.UPDATE_ACCESS_CONTROL({
					accessControlId: accessControlId,
					menuId: menuId,
					crud: crudStr,
				}),
			)
		}
	}

	const AuthSelector = ({
		accessControlId,
		menuId,
		thisCrudType,
		title,
		crud,
		isReadOnly,
	}: {
		accessControlId: string
		title: string
		menuId: string
		thisCrudType: 'C' | 'R' | 'U' | 'D'
		crud: CrudStrType
		isReadOnly: boolean
	}) => {
		const isChecked: boolean = crud.match(thisCrudType) !== null

		return (
			<View className="authSelectorView">
				<Pressable
					className="authSelectorButton"
					disabled={isDisabled || isReadOnly}
					onClick={() =>
						onAuthSelectorClick({
							accessControlId: accessControlId,
							menuId: menuId,
							prevCrud: crud,
							crudType: thisCrudType,
						})
					}
				>
					<span className="authSelectorLabel">{title}</span>
					<Icon
						name={isChecked ? 'checkbox-marked' : 'checkbox-blank-outline'}
						className={`authSelectorCheckIcon ${isChecked ? 'checked' : ''}`}
					/>
				</Pressable>
			</View>
		)
	}

	return (
		<View css={[style, { opacity: isDisabled ? 0.3 : 1 }]}>
			<DndTree
				data={menuTreeList}
				rootId="root"
				disableDragging
				nodeClassName="dndTreeNode"
				nodeRightAccessory={(aNode: MenuTreeNodeType) => {
					const menuId: string = aNode.id.toString() ?? aNode.id
					const thisAccessControl = accessControlList.filter(
						anAccessControl => anAccessControl.menuId === menuId,
					)
					const accessControlId: string =
						thisAccessControl.length === 0 ? 'new' : thisAccessControl[0].id

					const crudStr: CrudStrType =
						thisAccessControl.length === 0
							? ''
							: (thisAccessControl[0].crud.toUpperCase() as CrudStrType)

					return (
						<View className="nodeAccessoryView">
							<AuthSelector
								title="생성"
								accessControlId={accessControlId}
								menuId={menuId}
								thisCrudType="C"
								crud={crudStr}
								isReadOnly={isReadOnly}
							/>
							<AuthSelector
								title="읽기"
								accessControlId={accessControlId}
								menuId={menuId}
								thisCrudType="R"
								crud={crudStr}
								isReadOnly={isReadOnly}
							/>
							<AuthSelector
								title="수정"
								accessControlId={accessControlId}
								menuId={menuId}
								thisCrudType="U"
								crud={crudStr}
								isReadOnly={isReadOnly}
							/>
							<AuthSelector
								title="삭제"
								accessControlId={accessControlId}
								menuId={menuId}
								thisCrudType="D"
								crud={crudStr}
								isReadOnly={isReadOnly}
							/>
						</View>
					)
				}}
			/>
		</View>
	)
}

const style = css({
	width: '100%',
	overflow: 'auto',

	'.dndTreeNode': {
		position: 'relative',
		height: 40,
		marginBottom: 0,
		background: 'transparent',
		border: 0,
		borderBottom: `1px solid ${mono.line}`,
		borderRadius: 0,

		'.nodeAccessoryView': {
			position: 'absolute',
			display: 'flex',
			justifyContent: 'flex-end',
			gap: 2,
			height: '100%',
			right: 0,

			'.authSelectorView': {
				display: 'flex',
				alignItems: 'center',
				height: '100%',

				'.authSelectorButton': {
					display: 'flex',
					flexDirection: 'column',
					alignItems: 'center',
					gap: 1,
					width: 40,
					padding: 0,

					'.authSelectorLabel': {
						fontSize: 10,
						letterSpacing: '.08em',
						color: mono.faint,
						lineHeight: 1,
					},

					'.authSelectorCheckIcon': {
						fontSize: 18,
						color: mono.lineInput,
						transition: 'color 200ms ease-out',

						'&.checked': {
							color: mono.ink,
						},
					},

					'&:hover:not(:disabled) .authSelectorCheckIcon': { color: mono.ink },
				},
			},
		},
	},
})
